import { useState, useCallback, useEffect } from 'react';
import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold } from '@google/generative-ai';
import { interactionService } from '../services/supabase-service';
import { languageService } from '../services/language-service';
import { removeImageBackground } from '../utils/backgroundRemoval';
import { getCachedImage, cacheImage } from '../utils/imageUtils';

// API configuration

// Use environment variable for API key, with a fallback for local development
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const genAI = new GoogleGenerativeAI(API_KEY);
const CHAT_MODEL = 'models/gemini-2.5-flash';
const IMAGE_MODEL = 'imagen-4.0-generate-001'; // Imagen model for direct REST calls
const IMAGE_SERVICE_URL = process.env.REACT_APP_IMAGE_SERVICE_URL || '/.netlify/functions/generate-image';
const IMAGEN_API_BASE_URL = 'https://generativelanguage.googleapis.com/v1beta/models';

const buildImagenPayload = (prompt) => ({
  instances: [
    {
      prompt,
    },
  ],
  parameters: {
    sampleCount: 1,
    aspectRatio: '1:1',
  },
});

const getSafetyFilterReason = (payload) => {
  if (Array.isArray(payload?.filters) && payload.filters.length > 0) {
    return payload.filters
      .map(filter => filter?.reason || filter?.description)
      .filter(Boolean)
      .join(', ') || 'blocked by safety filters';
  }

  const filteredPrediction = payload?.predictions?.find(
    prediction => prediction?.raiFilteredReason || prediction?.safetyAttributes?.blocked
  );

  return filteredPrediction?.raiFilteredReason || null;
};

const extractImagenImageData = (payload) => {
  const prediction = payload?.predictions?.[0];
  const generatedImage = payload?.generatedImages?.[0];
  const imageRecord = payload?.images?.[0];

  const base64Data =
    prediction?.bytesBase64Encoded ||
    prediction?.image?.bytesBase64Encoded ||
    prediction?.image?.imageBytes ||
    generatedImage?.image?.imageBytes ||
    generatedImage?.image?.bytesBase64Encoded ||
    imageRecord?.image?.bytesBase64Encoded ||
    imageRecord?.image?.base64Data ||
    imageRecord?.image?.data ||
    imageRecord?.base64Data ||
    imageRecord?.data;

  const mimeType =
    prediction?.mimeType ||
    prediction?.image?.mimeType ||
    generatedImage?.image?.mimeType ||
    imageRecord?.mimeType ||
    'image/png';

  return { base64Data, mimeType };
};

const requestImagenGeneration = async (prompt, apiKey) => {
  if (!apiKey) {
    throw new Error('Missing REACT_APP_GEMINI_API_KEY for image generation.');
  }

  const imagenEndpoint = `${IMAGEN_API_BASE_URL}/${IMAGE_MODEL}:predict`;
  const response = await fetch(imagenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-goog-api-key': apiKey
    },
    body: JSON.stringify(buildImagenPayload(prompt))
  });

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null);
    const apiErrorMessage = errorPayload?.error?.message || response.statusText;
    throw new Error(`Imagen API request failed: ${apiErrorMessage}`);
  }

  const data = await response.json();

  const safetyFilterReason = getSafetyFilterReason(data);
  if (safetyFilterReason) {
    throw new Error(`Image generation blocked by safety filters: ${safetyFilterReason}`);
  }

  const { base64Data, mimeType } = extractImagenImageData(data);

  if (!base64Data) {
    throw new Error('Imagen API response did not include image data.');
  }

  return { base64Data, mimeType };
};

// Get language-specific prompts
const getSystemPrompt = (language) => {
  const prompts = {
    // English (default)
    en: `You are a Socratic programming tutor for middle school students using Scratch.
Your goal is to help students discover solutions themselves through questions, not by giving answers directly.
Keep your responses concise, encouraging, and focused on the student's learning process.
Ask guiding questions. Examples: "What do you think this block does?", "What have you tried so far?", "What happens if you change this value?", "What are you trying to make happen next?"
When analyzing images (like Scratch blocks or projects): Ask questions about the image to stimulate the student's thinking. "Can you explain what this part of the code is supposed to do?", "What does the green flag block usually start?"
If they ask for code directly: Guide them by asking what they want to achieve first. "What do you think is the first step we should take?"
Avoid writing Scratch code blocks, unless you're guiding them block-by-block after they've described the logic.
Be patient and positive. Make learning Scratch fun!`,

    // Danish
    da: `Du er en sokratisk programmeringsvejleder for elever i mellemtrinnet, der bruger Scratch.
Dit mål er at hjælpe eleverne med selv at opdage løsninger gennem spørgsmål, ikke ved at give svarene direkte.
Hold dine svar korte, opmuntrende og fokuserede på elevens læreproces.
Stil vejledende spørgsmål. Eksempler: "Hvad tror du, denne blok gør?", "Hvad har du prøvet indtil nu?", "Hvad sker der, hvis du ændrer denne værdi?", "Hvad prøver du at få til at ske bagefter?"
Når du analyserer billeder (som Scratch-blokke eller projekter): Stil spørgsmål om billedet for at stimulere elevens tænkning. "Kan du forklare, hvad denne del af koden skal gøre?", "Hvad starter den grønne flagblok normalt?"
Hvis de beder direkte om kode: Vejled dem ved først at spørge, hvad de vil opnå. "Hvad tror du, det første skridt bør være?"
Undgå at skrive Scratch-kodeblokke, medmindre du guider dem blok for blok, efter at de har beskrevet logikken.
Vær tålmodig og positiv. Gør det sjovt at lære Scratch!`,
    
    // Japanese
    ja: `あなたはScratchを使用している中学生向けのソクラテス式プログラミング講師です。
あなたの目標は、直接答えを与えるのではなく、質問を通じて生徒が自分で解決策を発見できるよう手助けすることです。
回答は簡潔で励みになり、生徒の学習プロセスに焦点を当てるようにしてください。
誘導的な質問をしましょう。例：「このブロックは何をすると思いますか？」「今までに何を試しましたか？」「この値を変更するとどうなりますか？」「次に何が起こるようにしようとしていますか？」
画像（Scratchブロックやプロジェクトなど）を分析する際：生徒の思考を刺激するために画像について質問をします。「このコードの部分は何をするはずですか？」「緑の旗ブロックは通常何を開始しますか？」
直接コードを求められた場合：まず何を達成したいのかを尋ねることで導きます。「最初のステップは何だと思いますか？」
生徒がロジックを説明した後に一歩ずつ指導する場合を除き、Scratchコードブロックを書くのは避けてください。
忍耐強くポジティブでいましょう。Scratchを学ぶことを楽しくしてください！`,
    
    // French
    fr: `Tu es un tuteur socratique de programmation pour les élèves de collège utilisant Scratch.
Ton objectif est d'aider les élèves à découvrir des solutions par eux-mêmes grâce à un questionnement, et non en donnant directement les réponses.
Garde tes réponses concises, encourageantes et concentrées sur le processus d'apprentissage de l'élève.
Pose des questions d'orientation. Exemples : "Que penses-tu que ce bloc fait ?", "Qu'as-tu essayé jusqu'à présent ?", "Que se passe-t-il si tu changes cette valeur ?", "Qu'essaies-tu de faire se produire ensuite ?"
Lors de l'analyse d'images (comme des blocs ou des projets Scratch) : Pose des questions sur l'image pour stimuler la réflexion de l'élève. "Peux-tu expliquer ce que cette partie du code est censée faire ?", "Que démarre habituellement le bloc drapeau vert ?"
S'ils demandent directement du code : Guide-les en leur demandant d'abord ce qu'ils veulent réaliser. "Quelle est la première étape que nous devons faire selon toi ?"
Évite d'écrire des blocs de code Scratch, sauf si tu les guides bloc par bloc après qu'ils aient décrit la logique.
Sois patient et positif. Rends l'apprentissage de Scratch amusant !`,
    
    // Spanish
    es: `Eres un tutor socratico de programacion para estudiantes de secundaria que utilizan Scratch.
Tu objetivo es ayudar a los estudiantes a descubrir soluciones por si mismos mediante preguntas, no dando respuestas directamente.
Manten tus respuestas concisas, alentadoras y centradas en el proceso de aprendizaje del estudiante.
Haz preguntas guía. Ejemplos: "¿Qué crees que hace este bloque?", "¿Qué has intentado hasta ahora?", "¿Qué sucede si cambias este valor?", "¿Qué estás tratando de hacer que ocurra a continuación?"
Al analizar imágenes (como bloques o proyectos de Scratch): Haz preguntas sobre la imagen para estimular el pensamiento del estudiante. "¿Puedes explicar qué se supone que hace esta parte del código?", "¿Qué suele iniciar el bloque de bandera verde?"
Si piden código directamente: Guíalos preguntando primero qué quieren lograr. "¿Cuál crees que es el primer paso que debemos tomar?"
Evita escribir bloques de código Scratch, a menos que los estés guiando bloque por bloque después de que hayan descrito la lógica.
Sé paciente y positivo. ¡Haz que aprender Scratch sea divertido!`,
    
    // German
    de: `Du bist ein sokratischer Programmier-Tutor fur Mittelschuler, die Scratch verwenden.
Dein Ziel ist es, Schulern zu helfen, Losungen durch Fragen selbst zu entdecken, nicht direkt Antworten zu geben.
Halte deine Antworten praegnant, ermutigend und konzentriert auf den Lernprozess des Schulers.
Stelle Leitfragen. Beispiele: "Was glaubst du, was dieser Block macht?", "Was hast du bisher versucht?", "Was passiert, wenn du diesen Wert anderst?", "Was versuchst du als nachstes zu erreichen?"
Bei der Analyse von Bildern (wie Scratch-Blocken oder Projekten): Stelle Fragen zum Bild, um das Denken des Schulers anzuregen. "Kannst du erklaren, was dieser Teil des Codes tun soll?", "Was startet normalerweise der grune Flaggen-Block?"
Wenn sie direkt nach Code fragen: Leite sie an, indem du zuerst fragst, was sie erreichen mochten. "Was ist deiner Meinung nach der erste Schritt, den wir machen sollten?"
Vermeide es, Scratch-Codeblocks zu schreiben, es sei denn, du fuhrst sie Schritt fur Schritt an, nachdem sie die Logik beschrieben haben.
Sei geduldig und positiv. Mache das Lernen von Scratch Spass!`,
    
    // Italian
    it: `Sei un tutor socratico di programmazione per studenti delle scuole medie che utilizzano Scratch.
Il tuo obiettivo e aiutare gli studenti a scoprire soluzioni da soli attraverso domande, non dando risposte direttamente.
Mantieni le tue risposte concise, incoraggianti e concentrate sul processo di apprendimento dello studente.
Poni domande guida. Esempi: "Cosa pensi che faccia questo blocco?", "Cosa hai provato finora?", "Cosa succede se cambi questo valore?", "Cosa stai cercando di far accadere dopo?"
Quando analizzi immagini (come blocchi o progetti Scratch): Fai domande sull'immagine per stimolare il pensiero dello studente. "Puoi spiegare cosa dovrebbe fare questa parte del codice?", "Cosa avvia solitamente il blocco bandiera verde?"
Se chiedono direttamente del codice: Guidali chiedendo prima cosa vogliono realizzare. "Quale pensi sia il primo passo che dovremmo fare?"
Evita di scrivere blocchi di codice Scratch, a meno che non li stai guidando blocco per blocco dopo che hanno descritto la logica.
Sii paziente e positivo. Rendi divertente l'apprendimento di Scratch!`,
    
    // Portuguese
    pt: `Voce e um tutor socratico de programacao para estudantes do ensino medio usando Scratch.
Seu objetivo e ajudar os alunos a descobrir solucoes por si mesmos atraves de perguntas, nao dando respostas diretamente.
Mantenha suas respostas concisas, encorajadoras e focadas no processo de aprendizado do aluno.
Faca perguntas orientadoras. Exemplos: "O que voce acha que este bloco faz?", "O que voce tentou ate agora?", "O que acontece se voce mudar este valor?", "O que voce esta tentando fazer acontecer em seguida?"
Ao analisar imagens (como blocos ou projetos Scratch): Faca perguntas sobre a imagem para estimular o pensamento do aluno. "Voce pode explicar o que esta parte do codigo deve fazer?", "O que geralmente inicia o bloco de bandeira verde?"
Se eles pedirem codigo diretamente: Guie-os perguntando primeiro o que eles querem realizar. "Qual voce acha que e o primeiro passo que devemos tomar?"
Evite escrever blocos de codigo Scratch, a menos que voce os guie bloco por bloco depois que eles descreverem a logica.
Seja paciente e positivo. Torne o aprendizado do Scratch divertido!`,
    
    // Romanian
    ro: `Esti un tutor socratic de programare pentru elevii de gimnaziu care folosesc Scratch.
Obiectivul tau este sa ajuti elevii sa descopere solutii prin intrebari, nu oferind raspunsuri direct.
Pastreaza raspunsurile tale concise, incurajatoare si concentrate pe procesul de invatare al elevului.
Pune intrebari orientative. Exemple: "Ce crezi ca face acest bloc?", "Ce ai incercat pana acum?", "Ce se intampla daca schimbi aceasta valoare?", "Ce incerci sa faci sa se intample in continuare?"
Cand analizezi imagini (cum ar fi blocuri sau proiecte Scratch): Pune intrebari despre imagine pentru a stimula gandirea elevului. "Poti explica ce ar trebui sa faca aceasta parte a codului?", "Ce porneste de obicei blocul steagului verde?"
Daca cer cod direct: Ghideaza-i intreband mai intai ce vor sa realizeze. "Care crezi ca este primul pas pe care ar trebui sa-l facem?"
Evita sa scrii blocuri de cod Scratch, cu exceptia cazului in care ii ghidezi bloc cu bloc dupa ce au descris logica.
Fii rabdator si pozitiv. Fa invatarea Scratch distractiva!`,
    
    // Russian
    ru: `Ty sokraticheskij repetitor po programmirovaniju dlja uchenikov srednej shkoly, ispol'zujushhih Scratch.
Tvoja cel' — pomoch' uchenikam samostojatel'no nahodit' reshenija cherez voprosy, a ne davaja otvety naprjamuju.
Delaj svoi otvety kratkimi, obodrjajushhimi i sosredotochennymi na processe obuchenija uchenika.
Zadavaj napravliajushhie voprosy. Primery: "Kak ty dumaesh', chto delaet etot blok?", "Chto ty proboval do sih por?", "Chto proizojdet, esli ty izmenish' eto znachenie?", "Chto ty pytaesh'sja sdelat' dal'she?"
Pri analize izobrazhenij (takih kak bloki ili proekty Scratch): Zadavaj voprosy ob izobrazhenii, chtoby stimulirovat' myshlenie uchenika. "Mozzhesh' ob'jasnit', chto dolzhna delat' eta chast' koda?", "Chto obychno zapuskaet blok zelenogo flaga?"
Esli oni prosjat kod naprjamuju: Napravliaj ih, snachala sprashivaja, chego oni hotjat dostich'. "Kakoj, po-tvoemu, pervyj shag, kotoryj my dolzzhny sdelat'?"
Izbegaj napisanija blokov koda Scratch, esli tol'ko ty ne vedesh' ih blok za blokom posle togo, kak oni opisali logiku.
Bud' terpelivym i pozitivnym. Delaj izuchenie Scratch uvlekatel'nym!`,
    
    // Korean
    ko: `Scratch를 사용하는 중학생을 위한 소크라테스식 프로그래밍 튜터입니다.
목표는 학생들이 질문을 통해 스스로 해결책을 발견하도록 돕는 것이지, 직접적인 답변을 제공하는 것이 아닙니다.
답변을 간결하고 격려하며 학생의 학습 과정에 초점을 맞추세요.
안내하는 질문을 하세요. 예시: "이 블록이 무엇을 한다고 생각하나요?", "지금까지 무엇을 시도해 보았나요?", "이 값을 변경하면 어떻게 될까요?", "다음에 무엇이 일어나기를 원하나요?"
이미지 (스크래치 블록 또는 프로젝트 등)를 분석할 때: 학생의 사고를 자극하기 위해 이미지에 관한 질문을 하세요. "이 코드 부분이 무엇을 하도록 되어 있는지 설명할 수 있나요?", "초록색 깃발 블록은 보통 무엇을 시작하나요?"
직접 코드를 요청하는 경우: 그들이 뭘 성취하고 싶은지 먼저 물어봐서 안내하세요. "어떤 첫 번째 단계가 필요하다고 생각하나요?"
학생이 논리를 설명한 후에 블록별로 안내하는 경우를 제외하고는 Scratch 코드 블록을 작성하지 마세요.
인내심을 가지고 긍정적으로 임하세요. Scratch 학습을 재미있게 만드세요!`,
    
    // Chinese
    zh: `你是使用苏格拉底式教学法的编程导师，为使用Scratch的中学生提供帮助。
目标是通过提问帮助学生自己发现解决方案，而不是直接给出答案。
保持回答简洁、鼓励性，并专注于学生的学习过程。
提出引导性问题。例如：“你认为这个积木是做什么的？”，“你到目前为止尝试了什么？”，“如果你改变这个值会发生什么？”，“你接下来想要实现什么？”
当分析图片（如Scratch积木或项目）时：提出关于图片的问题，以刺激学生思考。“你能解释一下代码的这部分应该做什么吗？”，“绿旗积木通常用来启动什么？”
如果他们直接要求代码：通过先询问他们想要实现什么来引导他们。“你认为我们应该采取的第一步是什么？”
避免编写Scratch代码块，除非在他们描述完逻辑后，你一步一步地引导他们。
保持耐心和积极态度。让学习Scratch变得有趣！`,
    
    // Arabic
    ar: `أنت مدرس برمجة سقراطي لطلاب المدارس المتوسطة الذين يستخدمون سكراتش.
هدفك هو مساعدة الطلاب على اكتشاف الحلول بأنفسهم من خلال الأسئلة، وليس إعطاء الإجابات مباشرة.
اجعل إجاباتك موجزة ومشجعة وتركز على عملية تعلم الطالب.
اطرح أسئلة إرشادية. أمثلة: "ماذا تعتقد أن هذه الكتلة تفعل؟", "ماذا جربت حتى الآن؟", "ماذا يحدث إذا غيرت هذه القيمة؟", "ماذا تحاول أن تجعل يحدث بعد ذلك؟"
عند تحليل الصور (مثل كتل سكراتش أو المشاريع): اطرح أسئلة حول الصورة لتحفيز تفكير الطالب. "هل يمكنك شرح ما يفترض أن يفعله هذا الجزء من الكود؟", "ماذا تبدأ عادة كتلة العلم الأخضر؟"
إذا طلبوا الكود مباشرة: وجههم بسؤالهم أولاً عما يريدون تحقيقه. "ما هي الخطوة الأولى التي تعتقد أننا يجب أن نتخذها؟"
تجنب كتابة كتل كود سكراتش، إلا إذا كنت توجههم كتلة بكتلة بعد أن يكونوا قد وصفوا المنطق.
كن صبوراً وإيجابياً. اجعل تعلم سكراتش ممتعاً!`,
    
    // Hindi
    hi: `आप स्क्रैच का उपयोग करने वाले मिडिल स्कूल छात्रों के लिए एक सुकराती प्रोग्रामिंग ट्यूटर हैं।
आपका लक्ष्य छात्रों को सीधे उत्तर देने के बजाय प्रश्नों के माध्यम से समाधान खुद खोजने में मदद करना है।
अपने उत्तरों को संक्षिप्त, प्रोत्साहित करने वाले और छात्र की सीखने की प्रक्रिया पर केंद्रित रखें।
मार्गदर्शक प्रश्न पूछें। उदाहरण: "आपको क्या लगता है कि यह ब्लॉक क्या करता है?", "अब तक आपने क्या प्रयास किया है?", "अगर आप इस मान को बदलें तो क्या होगा?", "आप आगे क्या होने की कोशिश कर रहे हैं?"
छवियों का विश्लेषण करते समय (जैसे स्क्रैच ब्लॉक या प्रोजेक्ट): छात्र की सोच को प्रेरित करने के लिए छवि के बारे में प्रश्न पूछें। "क्या आप बता सकते हैं कि कोड का यह हिस्सा क्या करना चाहिए?", "हरे झंडे का ब्लॉक आमतौर पर क्या शुरू करता है?"
अगर वे सीधे कोड मांगें: पहले यह पूछकर उनका मार्गदर्शन करें कि वे क्या हासिल करना चाहते हैं। "आपके अनुसार हमें पहला कदम क्या उठाना चाहिए?"
स्क्रैच कोड ब्लॉक लिखने से बचें, जब तक कि आप उनके लॉजिक को समझाने के बाद उन्हें ब्लॉक-बाई-ब्लॉक गाइड न कर रहे हों।
धैर्यवान और सकारात्मक बनें। स्क्रैच सीखना मज़ेदार बनाएं!`
  };
  
  return prompts[language] || prompts.en;
};

/**
 * Hook for managing chat and image generation with Gemini API
 */
const useChat = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(
    languageService.getLanguage()
  );

  /**
   * Update the user's preferred language
   * @param {string} languageCode - ISO language code
   */
  const changeLanguage = useCallback(async (languageCode) => {
    if (!languageCode) return;
    
    setCurrentLanguage(languageCode);
    await interactionService.updateLanguage(languageCode);
    
    // Add a system message indicating language change
    const languageName = languageService.getLanguageDisplayName(languageCode);
    const systemMessage = {
      sender: 'system',
      text: `Language changed to ${languageName}`
    };
    
    setMessages(prevMessages => [...prevMessages, systemMessage]);
  }, []); // Removed unnecessary currentLanguage dependency

  // Initialize language on component mount - only run once
  useEffect(() => {
    setCurrentLanguage(languageService.getLanguage());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove currentLanguage from dependencies to avoid infinite loop

  /**
   * Sends a chat message and processes the response
   */
  const sendMessage = useCallback(async ({ text, imageBase64 }) => {
    setIsLoading(true);
    setError(null);

    // Add user message optimistically
    const userMessage = { 
      sender: 'user', 
      text: text, 
      imageBase64: imageBase64
    };
  
    // Check for language change command
    const languageChangeRegex = /^(set|change) language( to)? ([a-z]{2})$/i;
    const match = text.match(languageChangeRegex);
  
    if (match) {
      const newLanguage = match[3].toLowerCase();
      const supportedLanguages = languageService.getSupportedLanguages();
      const isSupported = supportedLanguages.some(lang => 
        lang.code === newLanguage);
      
      if (isSupported) {
        await changeLanguage(newLanguage);
        
        // Add AI response about language change
        const languageName = languageService.getLanguageDisplayName(newLanguage);
        const aiResponse = {
          sender: 'ai',
          text: `I've changed the language to ${languageName}. How can I help you with Scratch programming?`
        };
        
        setMessages(prevMessages => 
          [...prevMessages, userMessage, aiResponse]);
        setIsLoading(false);
        
        return { response: aiResponse };
      }
    }
  
    // Log user message with current language
    interactionService.logInteraction('user_message', null, null, { 
      hasImage: !!imageBase64, 
      language: currentLanguage,
      text: text
    });
  
    setMessages(prevMessages => [...prevMessages, userMessage]);

    try {
      // Initialize the model
      const chat = genAI.getGenerativeModel({
        model: CHAT_MODEL,
      });

      // Prepare chat history - filter out system messages and empty messages
      let filteredMessages = messages
        .filter(msg => msg && (msg.text || msg.imageBase64) && (msg.sender === 'user' || msg.sender === 'ai'));
      
      // Ensure conversation starts with a user message to avoid the 'First content should be with role user, got model' error
      // If the first message in history is from AI, we'll skip it since Gemini API requires first message to be from user
      if (filteredMessages.length > 0 && filteredMessages[0].sender === 'ai') {
        filteredMessages = filteredMessages.slice(1);
      }
      
      const chatHistory = filteredMessages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'model',
        parts: [{ text: msg.text || '' }]
      }));

      // Prepare parts for user's message
      const currentUserParts = [];
      
      // Add user's text
      if (text) {
        currentUserParts.push({
          text: text
        });
      }
      
      // Add user's image if any
      if (imageBase64) {
        // Remove the MIME prefix if it exists
        const base64Data = imageBase64.includes('data:image') 
          ? imageBase64.split(',')[1] 
          : imageBase64;
          
        currentUserParts.push({
          inlineData: {
            mimeType: 'image/png',
            data: base64Data
          }
        });
      }
      
      // Format current message in the expected structure
      const currentMessage = currentUserParts.length > 0 ? { role: 'user', parts: currentUserParts } : null;

      // Combine history with current message if it exists
      const contents = currentMessage ? [...chatHistory, currentMessage] : [...chatHistory];

      // Validate that we have content to send
      if (contents.length === 0 || (currentMessage && currentMessage.parts.length === 0)) {
        console.error('[useChat] No valid content to send to Gemini.');
        setError('Empty message. Please type something or attach an image.');
        setIsLoading(false);
        return null;
      }

      // Safety settings for the chat
      const safetySettings = [
        {
          category: HarmCategory.HARM_CATEGORY_HARASSMENT,
          threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
        },
        {
          category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
          threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
        },
        {
          category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
          threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
        },
        {
          category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
          threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
        },
      ];

      // Generation settings to increase determinism and focus
      const generationConfig = {
        temperature: 0.7,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 800,
      };
      
      // Get the language name for the system prompt
      const languageName = languageService.getLanguageDisplayName(currentLanguage);

      // Start the chat with system instructions including language preference
      const systemPromptWithLanguage = getSystemPrompt(currentLanguage) + 
        `\n\nIMPORTANT: ALWAYS respond in ${languageName} (${currentLanguage}). The user has selected ${languageName} as their preferred language.`;
        
      // Create the chat session
      const chat_session = chat.startChat({
        history: contents,
        generationConfig,
        safetySettings,
        systemInstruction: {
          parts: [{ text: systemPromptWithLanguage }]
        }
      });
      
      // Send the message and get the response
      const result = await chat_session.sendMessage(currentUserParts);
      const response = result.response; 

      // Primary response validation
      if (!response) {
        console.error('[useChat] No response object received from Gemini.');
        const blockReason = result?.promptFeedback?.blockReason;
        const safetyRatings = result?.candidates?.[0]?.safetyRatings;
        console.error(`Block Reason: ${blockReason}`, `Safety Ratings: ${JSON.stringify(safetyRatings)}`);
        throw new Error(blockReason ? `Content blocked: ${blockReason}` : 'No response received from AI model.');
      }

      // Check for prompt feedback block
      if (response.promptFeedback?.blockReason) {
        console.error(`[useChat] Request blocked by API. Reason: ${response.promptFeedback.blockReason}`);
        throw new Error(`Request blocked due to safety settings. Reason: ${response.promptFeedback.blockReason}`);
      }

      // Check candidate validity and safety filter status
      const candidate = response.candidates?.[0];
      if (!candidate || candidate.finishReason === 'SAFETY') {
        const safetyRatings = candidate?.safetyRatings;
        console.error(`[useChat] Response filtered due to safety settings. Ratings: ${JSON.stringify(safetyRatings)}`);
        throw new Error('Response blocked due to safety settings.');
      }

      // Extract and validate response text
      const responseText = response.text();
      
      if (typeof responseText !== 'string') {
        console.error('[useChat] Could not extract valid text from Gemini response:', JSON.stringify(response, null, 2));
        throw new Error('Failed to get valid text content from response');
      }

      console.log('[useChat] Received valid response from Gemini API');

      // Log assistant message
      interactionService.logInteraction('assistant_message', null, null, { 
        text: responseText
      });

      // Add AI response to messages
      const aiMessage = { sender: 'ai', text: responseText };
      setMessages(prevMessages => [...prevMessages, aiMessage]);

    } catch (err) {
      console.error("Error sending message:", err);
      setError(err.message || 'Failed to get response from AI');
      
      // Add error message
      const errorMessage = { 
        sender: 'system', 
        text: `Error: ${err.message}` 
      };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [currentLanguage, changeLanguage, messages]);

  /**
   * Get localized text for image generation messages
   */
  const getLocalizedImageText = (language, type, prompt) => {
    const texts = {
      generating: {
        en: `Generating image: ${prompt}...`,
        es: `Generando imagen: ${prompt}...`,
        fr: `Génération d'image: ${prompt}...`,
        ja: `画像を生成中: ${prompt}...`,
        it: `Generazione immagine: ${prompt}...`,
        de: `Bild wird generiert: ${prompt}...`,
        pt: `Gerando imagem: ${prompt}...`,
        ro: `Generare imagine: ${prompt}...`,
        ru: `Создание изображения: ${prompt}...`,
        ko: `이미지 생성 중: ${prompt}...`,
        zh: `正在生成图像: ${prompt}...`,
        ar: `جاري إنشاء الصورة: ${prompt}...`,
        da: `Genererer billede: ${prompt}...`,
        hi: `छवि उत्पन्न कर रहा है: ${prompt}...`
      },
      processing: {
        en: 'Removing background from generated image...',
        es: 'Eliminando el fondo de la imagen generada...',
        fr: 'Suppression de l\'arrière-plan de l\'image générée...',
        ja: '生成された画像から背景を削除しています...',
        it: 'Rimozione dello sfondo dall\'immagine generata...',
        de: 'Hintergrund wird vom generierten Bild entfernt...',
        pt: 'Removendo o fundo da imagem gerada...',
        ro: 'Se elimina fundalul din imaginea generata...',
        ru: 'Удаление фона с созданного изображения...',
        ko: '생성된 이미지에서 배경 제거중...',
        zh: '正在移除生成图像的背景...',
        ar: 'جاري إزالة الخلفية من الصورة المنشأة...',
        da: 'Fjerner baggrunden fra det genererede billede...',
        hi: 'उत्पन्न छवि से पृष्ठभूमि हटा रहा है...'
      },
      generated: {
        en: `Generated image: ${prompt}`,
        es: `Imagen generada: ${prompt}`,
        fr: `Image générée: ${prompt}`,
        ja: `生成された画像: ${prompt}`,
        it: `Immagine generata: ${prompt}`,
        de: `Generiertes Bild: ${prompt}`,
        pt: `Imagem gerada: ${prompt}`,
        ro: `Imagine generata: ${prompt}`,
        ru: `Созданное изображение: ${prompt}`,
        ko: `생성된 이미지: ${prompt}`,
        zh: `生成的图像: ${prompt}`,
        ar: `الصورة المنشأة: ${prompt}`,
        da: `Genereret billede: ${prompt}`,
        hi: `उत्पन्न छवि: ${prompt}`
      }
    };
    
    // Return the localized text or fall back to English
    return texts[type][language] || texts[type].en;
  };

  /**
   * Generates an image based on a prompt
   */
  const generateImage = useCallback(async (prompt) => {
    setIsLoading(true);
    setError(null);
    
    // Ensure we don't have double colons in the prompt
    const cleanPrompt = prompt.replace(/^:+\s*/, '');
    
    console.log(`[useChat] Requesting image generation for prompt: ${cleanPrompt}`);

    // Log image generation request
    interactionService.logInteraction('image_generation', null, null, { 
      prompt: cleanPrompt
    });

    // Add system message indicating image generation start with localized text
    const systemMessageStart = { 
      sender: 'system', 
      text: getLocalizedImageText(currentLanguage, 'generating', cleanPrompt)
    };
    setMessages(prevMessages => [...prevMessages, systemMessageStart]);

    try {
      // Check if we have a cached image for this prompt
      const cachedImage = getCachedImage(cleanPrompt);
      if (cachedImage) {
        console.log(`[useChat] Found cached image for prompt: ${cleanPrompt.substring(0, 30)}...`);
        
        // Update the message to indicate we're processing the cached image
        const processingMessage = {
          sender: 'system',
          text: getLocalizedImageText(currentLanguage, 'processing', cleanPrompt)
        };
        setMessages(prevMessages => [
          ...prevMessages.filter(msg => msg.text !== getLocalizedImageText(currentLanguage, 'generating', cleanPrompt)),
          processingMessage
        ]);
        
        // Process cached image to remove background
        let processedImage;
        try {
          console.log('[useChat] Starting background removal for cached image');
          processedImage = await removeImageBackground(cachedImage, {
            model: 'medium',
            debug: false,
            progress: (progress) => console.log(`[useChat] Background removal progress (cached): ${Math.round(progress * 100)}%`)
          });
          console.log('[useChat] Background removal completed successfully for cached image');
        } catch (bgError) {
          console.error('[useChat] Background removal failed for cached image:', bgError);
          // Fall back to the original cached image without background removal
          processedImage = cachedImage;
        }
        
        // Add the cached image to chat with processing completed message
        const aiImageMessage = {
          sender: 'ai',
          imageBase64: processedImage,
          text: getLocalizedImageText(currentLanguage, 'generated', cleanPrompt) + ' (cached)'
        };
        
        // Update messages
        setMessages(prevMessages => [
          ...prevMessages.filter(msg => 
            msg.text !== getLocalizedImageText(currentLanguage, 'generating', cleanPrompt) &&
            msg.text !== getLocalizedImageText(currentLanguage, 'processing', cleanPrompt)
          ),
          aiImageMessage
        ]);
        
        // Log cache hit for analytics
        interactionService.logInteraction('image_generation_cache_hit', null, null, { 
          prompt: cleanPrompt
        });
        
        return;
      }
      
      // No cache hit, proceed with normal image generation
      // Style the prompt for better results with the Scratch project context
      // Style for clean, isolated objects that will work better with background removal
      // Get localized styling instructions based on the user's language
      const styleInstructions = {
        en: "2D suitable for middle school Scratch projects, high quality, clean isolated object",
        es: "2D adecuado para proyectos Scratch de escuela secundaria, alta calidad, objeto aislado limpio",
        fr: "2D adapté aux projets Scratch de collège, haute qualité, objet isolé net",
        ja: "中学校のScratchプロジェクトに適した2D、高品質、クリーンな単体オブジェクト",
        it: "2D adatto per progetti Scratch di scuola media, alta qualità, oggetto isolato pulito",
        de: "2D geeignet für Scratch-Projekte der Mittelstufe, hohe Qualität, sauberes isoliertes Objekt",
        ro: "2D potrivit pentru proiecte Scratch de gimnaziu, calitate înaltă, obiect izolat curat",
        pt: "2D adequado para projetos Scratch do ensino fundamental, alta qualidade, objeto isolado limpo",
        ru: "2D подходит для школьных проектов Scratch, высокое качество, чистый изолированный объект",
        ko: "중학교 Scratch 프로젝트에 적합한 2D, 고품질, 깨끗하게 분리된 객체",
        zh: "2D适合中学Scratch项目，高质量，干净单独的物体",
        ar: "ثنائي الأبعاد مناسب لمشاريع سكراتش للمدرسة المتوسطة، جودة عالية، كائن معزول نظيف",
        da: "2D egnet til Scratch-projekter i mellemtrinnet, høj kvalitet, rent isoleret objekt",
        hi: "2D मिडिल स्कूल स्क्रैच प्रोजेक्ट के लिए उपयुक्त, उच्च गुणवत्ता, साफ अलग वस्तु"
      };
      
      // Get styling in current language or fallback to English
      const styleText = styleInstructions[currentLanguage] || styleInstructions.en;
      
      // Combine the user's prompt with the localized styling instructions
      const styledPrompt = `${cleanPrompt}, ${styleText}`;
      console.log(`[useChat] Using styled prompt: ${styledPrompt}`);

      let rawImageBase64;

      if (IMAGE_SERVICE_URL) {
        try {
          const serviceResponse = await fetch(IMAGE_SERVICE_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              prompt: cleanPrompt,
              styledPrompt,
              language: currentLanguage
            })
          });

          const responseText = await serviceResponse.text();
          const serviceData = responseText ? JSON.parse(responseText) : {};

          if (!serviceResponse.ok) {
            const apiErrorMessage = serviceData?.error || serviceResponse.statusText;
            console.error('[useChat] Image service request failed:', serviceData || serviceResponse.statusText);
            const serviceError = new Error(`Image generation failed: ${apiErrorMessage}`);
            serviceError.allowFallback = serviceResponse.status === 404 || serviceResponse.status >= 500;
            throw serviceError;
          }

          const base64Data = serviceData?.image;
          const mimeType = serviceData?.mimeType || 'image/png';

          if (!base64Data) {
            console.error('[useChat] Missing base64 image data in service response', serviceData);
            throw new Error('Failed to generate image: Missing image data in response');
          }

          rawImageBase64 = `data:${mimeType};base64,${base64Data}`;
          console.log('[useChat] Successfully generated image via configured backend service, processing background removal...');
        } catch (serviceError) {
          if (serviceError.allowFallback === false) {
            throw serviceError;
          }
          if (!API_KEY) {
            throw serviceError;
          }
          console.warn('[useChat] Image service unavailable; falling back to direct Imagen API request.', serviceError);
        }
      }

      if (!rawImageBase64) {
        const { base64Data, mimeType } = await requestImagenGeneration(styledPrompt, API_KEY);
        rawImageBase64 = `data:${mimeType};base64,${base64Data}`;
        console.log('[useChat] Successfully generated image via Imagen 4 API, processing background removal...');
      }
      
      // Cache the raw image for future requests with the same prompt
      cacheImage(cleanPrompt, rawImageBase64);
      
      // Add initial message to show the image is being processed with localized text
      const processingMessage = {
        sender: 'system',
        text: getLocalizedImageText(currentLanguage, 'processing', cleanPrompt)
      };
      setMessages(prevMessages => [
        ...prevMessages.filter(msg => msg.text === getLocalizedImageText(currentLanguage, 'generating', cleanPrompt)),
        processingMessage
      ]);

      // Process the image to remove background using @imgly/background-removal
      let imageBase64;
      try {
        console.log('[useChat] Starting background removal process');
        // Process the image with our background removal utility
        imageBase64 = await removeImageBackground(rawImageBase64, {
          model: 'medium', // Use medium quality for a balance of speed and quality
          debug: false,
          progress: (progress) => console.log(`[useChat] Background removal progress: ${Math.round(progress * 100)}%`)
        });
        console.log('[useChat] Background removal completed successfully');
      } catch (bgError) {
        // Detailed error logging
        console.error('[useChat] Background removal failed with error:', bgError);
        console.error('[useChat] Error stack:', bgError.stack);
        console.error('[useChat] Falling back to original image');
        // Fall back to the original image if background removal fails
        imageBase64 = rawImageBase64;
      }

      // Add AI response with the generated image using localized text
      const aiImageMessage = {
        sender: 'ai',
        imageBase64: imageBase64,
        text: getLocalizedImageText(currentLanguage, 'generated', cleanPrompt)
      };
      
      // Remove both the "Generating..." and "Removing background..." messages and add the result
      setMessages(prevMessages => [
        ...prevMessages.filter(msg => 
          msg.text !== getLocalizedImageText(currentLanguage, 'generating', cleanPrompt) &&
          msg.text !== getLocalizedImageText(currentLanguage, 'processing', cleanPrompt)
        ),
        aiImageMessage
      ]);

    } catch (err) {
      console.error("Error generating image:", err);
      setError(err.message || 'Failed to generate image');
      
      // Add error message
      const errorMessage = { 
        sender: 'system', 
        text: `Error generating image: ${err.message}` 
      };
      
      // Remove the "Generating..." message and add the error message
      setMessages(prevMessages => 
        prevMessages.filter(msg => msg.text !== getLocalizedImageText(currentLanguage, 'generating', cleanPrompt))
          .concat([errorMessage])
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentLanguage]);

  // Return state and functions needed by the component
  return { 
    messages, 
    isLoading, 
    error, 
    sendMessage, 
    generateImage, 
    setMessages,
    changeLanguage
  };
};

export default useChat;
